import React, { FC, useState } from "react";
import nyc_sunrise from "../../assets/media/nyc_sunrise.mp4";

const LandingPage: FC = () => {
  const [showToast, setShowToast] = useState(false);

  const copyEmail = async () => {
    await navigator.clipboard.writeText("hello@ddl-labs.com");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  return (
    <div className="min-h-screen w-screen bg-neutral-950 text-white overflow-hidden">
      {/* Video Background */}
      <div className="fixed inset-0 z-0">
        <video
          src={nyc_sunrise}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-30"
        >
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/80 via-neutral-950/60 to-neutral-950/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="w-full px-8 md:px-16 py-8">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-white rounded-full" />
            <span className="text-sm font-light tracking-[0.3em] uppercase">
              DDL Labs
            </span>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-grow flex flex-col items-center justify-center px-8 md:px-16 py-12">
          {/* Hero */}
          <div className="text-center mb-20 max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-light tracking-wide mb-6">
              DDL Labs
            </h1>
            <p className="text-lg md:text-xl text-neutral-400 font-light tracking-wide">
              Building the future through applied AI
            </p>
          </div>

          {/* Companies */}
          <div className="w-full max-w-5xl">
            <p className="text-xs tracking-[0.25em] uppercase text-neutral-500 mb-8 text-center">
              Our Companies
            </p>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {/* Peep */}
              <a
                href="https://www.downloadpeep.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-8 md:p-10 rounded-2xl border border-neutral-800 bg-neutral-900/50 backdrop-blur-sm hover:border-neutral-600 hover:bg-neutral-900/80 transition-all duration-500"
              >
                <div className="absolute top-8 right-8 w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-orange-400 opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="mb-6">
                  <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-2">
                    Peep
                  </h2>
                  <span className="text-xs tracking-[0.2em] uppercase text-neutral-500">
                    Social Media
                  </span>
                </div>
                <p className="text-neutral-400 font-light leading-relaxed">
                  Automatic photo sharing. Capture moments effortlessly and let
                  AI curate your story.
                </p>
                <div className="mt-8 flex items-center text-sm text-neutral-500 group-hover:text-white transition-colors">
                  <span className="mr-2">Learn more</span>
                  <svg
                    className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </div>
              </a>

              {/* Enigma */}
              <a
                href="#enigma"
                className="group relative p-8 md:p-10 rounded-2xl border border-neutral-800 bg-neutral-900/50 backdrop-blur-sm hover:border-neutral-600 hover:bg-neutral-900/80 transition-all duration-500"
              >
                <div className="absolute top-8 right-8 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="mb-6">
                  <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-2">
                    Enigma
                  </h2>
                  <span className="text-xs tracking-[0.2em] uppercase text-neutral-500">
                    Intelligence Platform
                  </span>
                </div>
                <p className="text-neutral-400 font-light leading-relaxed">
                  Global intelligence and forecasting engine. Decode complexity,
                  predict outcomes.
                </p>
                <div className="mt-8 flex items-center text-sm text-neutral-500 group-hover:text-white transition-colors">
                  <span className="mr-2">Learn more</span>
                  <svg
                    className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </div>
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="mt-20">
            <button
              onClick={copyEmail}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-neutral-700 text-sm tracking-wide hover:bg-white hover:text-neutral-950 transition-all duration-300 cursor-pointer"
            >
              <span>hello@ddl-labs.com</span>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>

          {/* Toast */}
          <div
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 bg-white text-neutral-950 text-sm rounded-full shadow-lg transition-all duration-300 ${
              showToast
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4 pointer-events-none"
            }`}
          >
            Email copied to clipboard
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full px-8 md:px-16 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-xs text-neutral-600 space-y-4 md:space-y-0">
            <p>© {new Date().getFullYear()} DDL Labs, Inc.</p>
            <div className="flex items-center space-x-6">
              <span>+1 (408) 341-9215</span>
              <span>Stanford, CA</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
